/*
 * Stacked-gallery product layout — only the behaviour that layout
 * adds. Variant selection itself is NOT reimplemented here: the option cards carry
 * .js-product-size / .js-product-color and data-option / data-value, so product-page.js's
 * existing handlers drive the hidden Shopify.OptionSelectors <select> for us, and the price
 * row, the sold-out swap and the ?variant= history state all come for free.
 *
 * This file listens for the `product:variant-change` event product-page.js dispatches at
 * the end of its selectCallback, and does three things the carousel layout has no
 * equivalent for:
 *
 *   1. recomputes the per-value price on each size card, and the availability of every
 *      card (both belong to a variant, i.e. Size x Frame, so each restates itself
 *      whenever the other option moves);
 *   2. keeps the price baked into the add to cart label in step;
 *   3. on mobile, scrolls the gallery to the media for the chosen variant.
 *
 * HARD CONSTRAINT: product-page.js binds its click handlers directly to the card anchors,
 * once, at construction time. Nothing here may replace a card node — only rewrite its text
 * and toggle its classes — or those cards silently stop selecting anything.
 *
 * Loaded with `defer` BEFORE product-page.js, because Shopify.OptionSelectors fires
 * selectCallback synchronously while that file is still evaluating; the listener has to
 * already be attached. Everything it would set is also server-rendered, so the page is
 * correct with this file absent or slow.
 */
(function () {
  'use strict';

  var MOBILE = '(max-width: 991px)';

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      // The same call selectCallback makes. moneyWithCurrencyFormat is built in
      // theme.liquid as "<symbol>{{amount}} <iso_code>" — the JS twin of the
      // money-market snippet, and the reason we never use money_with_currency.
      return window.Shopify.formatMoney(cents, window.moneyWithCurrencyFormat);
    }
    return '';
  }

  /*
   * Without the currency code, for the per-value prices inside the option selectors: every
   * row there is the same currency as the one beside it, so the code is noise. Mirrors the
   * plain `money` filter those prices are rendered with on first paint.
   *
   * moneyMarketFormat, not the built-in moneyFormat: that one comes from shop.money_format
   * and carries the BASE currency's symbol, which is wrong in a converted market.
   */
  function formatMoneyPlain(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.moneyMarketFormat || window.moneyWithCurrencyFormat);
    }
    return '';
  }

  function productData(root) {
    var el = document.getElementById('ProductJson' + root.getAttribute('data-product-id'));
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      // A malformed blob should cost us the live prices, not the page.
      return null;
    }
  }

  /*
   * Mirrors the Liquid that renders these prices on first paint. Prefer the variant that
   * keeps every OTHER option where it is, so a card price describes what the shopper would
   * get by changing only this one thing. Fall back to the cheapest variant carrying the
   * value, flagged "from".
   */
  function priceForValue(variants, optionIndex, value, selectedOptions) {
    var i;
    var v;

    for (i = 0; i < variants.length; i++) {
      v = variants[i];
      var exact = true;
      for (var j = 0; j < selectedOptions.length; j++) {
        var want = j === optionIndex ? value : selectedOptions[j];
        if (v.options[j] !== want) {
          exact = false;
          break;
        }
      }
      if (exact) {
        return { price: v.price, available: v.available, isFrom: false };
      }
    }

    var best = null;
    for (i = 0; i < variants.length; i++) {
      v = variants[i];
      if (v.options[optionIndex] !== value) continue;
      if (!best) {
        best = { price: v.price, available: v.available, isFrom: true };
        continue;
      }
      // An available variant always beats an unavailable one; between two of equal
      // availability, the cheaper wins.
      if ((v.available && !best.available) || (v.available === best.available && v.price < best.price)) {
        best = { price: v.price, available: v.available, isFrom: true };
      }
    }
    return best;
  }

  function repriceCards(root, variant) {
    var steps = root.querySelector('.product-v2__steps');
    if (!steps) return;

    var data = productData(root);
    if (!data || !data.variants || !variant || !variant.options) return;

    var fromLabel = steps.getAttribute('data-from-label') || '';
    var selected = variant.options;

    // Frame cards print no price — only their availability restates itself, since whether
    // a finish can be bought depends on the size sitting beside it.
    steps.querySelectorAll('.product-v2__cards--frame .product-v2__card').forEach(function (card) {
      var option = card.getAttribute('data-option');
      var value = card.getAttribute('data-value');
      // "option1" -> 0
      var optionIndex = parseInt(option.replace('option', ''), 10) - 1;
      if (isNaN(optionIndex)) return;

      var result = priceForValue(data.variants, optionIndex, value, selected);
      if (!result) return;

      card.classList.toggle('is-unavailable', !result.available);
    });

    // Size cards print a price, so theirs is rewritten in place — the price span is
    // updated, never the card, because product-page.js bound its click handler to the
    // anchor at construction time and replacing that node would silently unbind it.
    // Availability is toggled from the same lookup, so a size and its frame agree.
    steps.querySelectorAll('.js-v2-card-price').forEach(function (priceEl) {
      var option = priceEl.getAttribute('data-option');
      var value = priceEl.getAttribute('data-value');
      // "option1" -> 0
      var optionIndex = parseInt(option.replace('option', ''), 10) - 1;
      if (isNaN(optionIndex)) return;

      var result = priceForValue(data.variants, optionIndex, value, selected);
      if (!result) return;

      var money = formatMoneyPlain(result.price);
      if (!money) return;

      priceEl.textContent = result.isFrom && fromLabel ? fromLabel + ' ' + money : money;

      var card = priceEl.closest('.product-v2__card');
      if (card) card.classList.toggle('is-unavailable', !result.available);
    });
  }

  /*
   * The current value beside each step title — "Frame: Black", "Size: Medium". Size drops
   * its bracketed measurement, as the Liquid does on first paint, because the selected row
   * under the title already shows it.
   */
  function updateStepValues(root, variant) {
    if (!variant || !variant.options) return;
    root.querySelectorAll('.js-v2-step-value').forEach(function (el) {
      // "option1" -> 0
      var optionIndex = parseInt((el.getAttribute('data-option') || '').replace('option', ''), 10) - 1;
      var value = variant.options[optionIndex];
      if (value == null) return;
      el.textContent = String(value).split(' (')[0];
    });
  }

  /*
   * drawer-cart.js overwrites the button with data-adding-text on click and restores it
   * from data-original-text after a successful add, so the price cannot live in a child
   * element — it has to BE the label, in both the text and the attribute.
   */
  function updateAtcLabel(root, variant) {
    root.querySelectorAll('.js-add-to-cart[data-atc-base-text]').forEach(function (button) {
      var money = formatMoney(variant.price);
      if (!money) return;
      var label = button.getAttribute('data-atc-base-text') + ' — ' + money;
      button.textContent = label;
      button.setAttribute('data-original-text', label);
    });
  }

  /*
   * Leads the gallery with the chosen variant's medium — the first slide, which is then
   * scrolled into view. Picking a frame therefore shows that frame, which is the whole
   * point of the swatch.
   *
   * MOBILE ONLY. Above 991px the gallery is the carousel layout's Splide gallery, and
   * selectCallback in product-page.js already moves it by calling splide.go() on the slide
   * carrying the variant's media id — the same mechanism the other arm uses, so the two
   * behave identically on desktop. Reordering the <li>s underneath a mounted Splide would
   * be actively wrong: Splide caches its slides with their index at mount and writes the
   * track transform from that, so moving nodes leaves it showing the wrong slide and
   * reporting the wrong index to the thumbnail rail.
   *
   * Tested by asking whether Splide is mounted rather than by matching the media query.
   * product-page.js mounts and destroys on the same 991px boundary, and reading the state
   * it actually left behind cannot disagree with it the way a second copy of the
   * breakpoint could.
   *
   * Reordering rather than rewriting an <img>: the medium is already in the list, so
   * moving it avoids loading the same file twice and keeps one node per medium, which is
   * what PhotoSwipe counts to work out which slide it opened. PhotoSwipe reads the DOM at
   * click time, so it follows the new order on its own.
   *
   * Always rebuilt from the server-rendered order rather than swapping pairs, so repeated
   * selections cannot shuffle the rest of the gallery into an arbitrary permutation.
   */
  function leadWithVariantMedia(root, variant) {
    if (!root.dataset.v2Interacted) return;
    if (!variant.featured_media) return;

    var carousel = root.querySelector('.js-product-media-carousel');
    if (carousel && (carousel.splideInstance || carousel.classList.contains('is-initialized'))) return;

    var list = root.querySelector('.js-v2-media-list');
    if (!list || !list.v2Order) return;

    var target = null;
    for (var i = 0; i < list.v2Order.length; i++) {
      if (list.v2Order[i].getAttribute('data-media-id') === String(variant.featured_media.id)) {
        target = list.v2Order[i];
        break;
      }
    }
    if (!target) return;

    var ordered = [target];
    for (var j = 0; j < list.v2Order.length; j++) {
      if (list.v2Order[j] !== target) ordered.push(list.v2Order[j]);
    }

    // appendChild on an element already in the list moves it, so this reorders in place
    // without detaching anything.
    ordered.forEach(function (li, index) {
      list.appendChild(li);

      // No sizes to swap any more: the desktop grid had a wide lead cell and narrow ones
      // under it, so a promoted image needed its sizes rewritten to stop the browser
      // serving the candidate it picked for a quarter-width box. Every slide is the same
      // width in both layouts now, so one sizes value from the Liquid covers the lot.
      if (index !== 0) return;
      var img = li.querySelector('img');
      // Promoted out of the lazy tail and into view in the same frame.
      if (img) img.loading = 'eager';
    });

    if (window.matchMedia(MOBILE).matches) {
      list.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }

  /*
   * Dot pagination for the mobile scroll-snap strip. The dots are server-rendered so the
   * row does not pop in and shift the buy box; this moves .is-active and makes them
   * operable. An observer rather than a scroll handler so it costs nothing while the strip
   * is still.
   *
   * The slides run full width with no peek, so this row is the only thing telling a
   * shopper the gallery continues — which is why it is worth wiring up rather than leaving
   * as a readout. Tapping scrolls; the observer then moves .is-active when the scroll
   * lands, so there is one writer for that class and a tap and a swipe cannot disagree.
   */
  function initDots(root) {
    var list = root.querySelector('.js-v2-media-list');
    var dotWrap = root.querySelector('.js-v2-dots');
    if (!list || !dotWrap || !('IntersectionObserver' in window)) return;

    var slides = Array.prototype.slice.call(list.children);
    var dots = Array.prototype.slice.call(dotWrap.children);
    if (slides.length !== dots.length) return;

    // Remembered so leadWithVariantMedia can rebuild from it instead of permuting whatever
    // order the last selection happened to leave behind.
    list.v2Order = slides;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          // Read position from the live DOM, not the snapshot above: selecting a variant
          // reorders these, and a stale index lights the wrong dot.
          var index = Array.prototype.indexOf.call(list.children, entry.target);
          if (index < 0) return;
          dots.forEach(function (dot, i) {
            dot.classList.toggle('is-active', i === index);
          });
        });
      },
      { root: list, threshold: 0.6 }
    );

    slides.forEach(function (slide) {
      observer.observe(slide);
    });

    // Position from the live DOM for the same reason the observer reads it there:
    // selecting a frame reorders the slides, so dot i means "whatever is in slot i now".
    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        var target = list.children[index];
        var first = list.firstElementChild;
        if (!target || !first) return;

        // Difference of two offsetLefts against the same offsetParent, rather than
        // index * clientWidth: it stays right if the strip ever gains a gap, and it does
        // not care what the slide width is.
        list.scrollTo({ left: target.offsetLeft - first.offsetLeft, behavior: 'smooth' });
      });
    });
  }

  /*
   * Mirrors the galleryFollowsVariant guard in product-page.js: only a real click should
   * move the gallery. Delegated, so it survives however the cards were rendered.
   *
   * Frame cards only — .js-product-color, which in this layout is the frame and nothing
   * else (every other option's cards carry .js-product-size). Deliberately NOT the size
   * cards, even though they also change the variant: no image in the gallery is
   * size-specific, so re-leading on a size change can only ever repeat the picture already
   * showing — except when settings.pdp_v2_lead_frame put a different frame there on
   * purpose, where it would throw that away on the shopper's first interaction, before
   * they have said anything about a frame at all. Once a frame HAS been picked this flag
   * stays set, so a later size change still keeps the gallery in step.
   */
  document.addEventListener(
    'click',
    function (event) {
      var card = event.target.closest('.js-product-color');
      if (!card) return;
      var root = card.closest('.product-v2');
      if (root) root.dataset.v2Interacted = 'true';
    },
    true
  );

  document.addEventListener('product:variant-change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant) return;

    // selectCallback dispatches on the .js-product element itself, but resolve by id
    // rather than relying on the target so this holds if the event is ever re-fired.
    var productId = event.detail.productId;
    var root =
      (productId && document.querySelector('.product-v2.js-product[data-product-id="' + productId + '"]')) ||
      document.querySelector('.product-v2');
    if (!root) return;

    repriceCards(root, variant);
    updateStepValues(root, variant);
    updateAtcLabel(root, variant);
    leadWithVariantMedia(root, variant);
  });

  /*
   * Header contrast over the mobile gallery. The header is transparent there (body.pdp-v2 in
   * section-header.scss), so black marks vanish over a dark room shot. This measures the strip
   * of the visible slide that sits under the header and sets body.pdp-header-reversed — white
   * marks — when white would out-contrast black there.
   *
   * Measured from a 48px-wide copy of the same image rather than the rendered <img>: drawing
   * the page's own image to a canvas taints it unless it was requested with CORS, and asking
   * for the full-size one with crossOrigin would download it a second time. The small copy
   * is ~1KB, fetched once per slide on first sight and cached by media id.
   *
   * The strip is mapped through object-fit: cover, so it is the part of the photo actually
   * under the header, not the top of the file. Frame mockups need no measuring: on mobile
   * their box starts below the header, so what is behind it is the slot's own light grey.
   *
   * Black whenever anything fails — no image, a video, a CDN that refuses CORS — which is
   * exactly the header as it was before this existed.
   */
  var HEADER_HEIGHT = 64;
  var REVERSED_CLASS = 'pdp-header-reversed';
  var SAMPLE_WIDTH = 48;
  var headerTone = {};

  function setHeaderReversed(reversed) {
    document.body.classList.toggle(REVERSED_CLASS, !!reversed);
  }

  // WCAG relative luminance of one sRGB pixel.
  function luminance(r, g, b) {
    var c = [r, g, b].map(function (v) {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  }

  function measureSlide(slide, done) {
    var img = slide.querySelector('img');
    if (!img || img.classList.contains('is-frame-mockup')) return done(false);

    var box = slide.querySelector('.product-v2__media-inner') || slide;
    var boxW = box.clientWidth;
    var boxH = box.clientHeight;
    var src = img.currentSrc || img.src;
    if (!boxW || !boxH || !src) return done(false);

    var url;
    try {
      url = new URL(src, window.location.href);
    } catch (e) {
      return done(false);
    }
    url.searchParams.set('width', SAMPLE_WIDTH);

    var sample = new Image();
    sample.crossOrigin = 'anonymous';
    sample.onerror = function () { done(false); };
    sample.onload = function () {
      try {
        var iw = sample.naturalWidth;
        var ih = sample.naturalHeight;
        var canvas = document.createElement('canvas');
        canvas.width = iw;
        canvas.height = ih;
        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(sample, 0, 0);

        // object-fit: cover, centred: the scale that fills the box, and how far the image
        // overhangs it on each axis. The header strip (0..boxW, 0..64) mapped back into the
        // sample's own pixels.
        var scale = Math.max(boxW / iw, boxH / ih);
        var offsetX = (boxW - iw * scale) / 2;
        var offsetY = (boxH - ih * scale) / 2;
        var x0 = Math.max(0, Math.floor((0 - offsetX) / scale));
        var x1 = Math.min(iw, Math.ceil((boxW - offsetX) / scale));
        var y0 = Math.max(0, Math.floor((0 - offsetY) / scale));
        var y1 = Math.min(ih, Math.max(y0 + 1, Math.ceil((HEADER_HEIGHT - offsetY) / scale)));

        var data = ctx.getImageData(x0, y0, x1 - x0, y1 - y0).data;
        var total = 0;
        var count = 0;
        for (var i = 0; i < data.length; i += 4) {
          total += luminance(data[i], data[i + 1], data[i + 2]);
          count++;
        }
        if (!count) return done(false);

        // White wins where its contrast ratio against the strip beats black's:
        // (1.05) / (L + 0.05) > (L + 0.05) / 0.05, which crosses at L ≈ 0.179.
        var mean = total / count;
        done(1.05 / (mean + 0.05) > (mean + 0.05) / 0.05);
      } catch (e) {
        // A tainted canvas throws on getImageData — the CDN did not send CORS headers.
        done(false);
      }
    };
    sample.src = url.toString();
  }

  function applyHeaderTone(slide) {
    // Held until the page has finished loading, so the first sample never queues against
    // the lead image it is measuring. The header just stays black until then.
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () {
        if (slide.dataset.headerActive === 'true') applyHeaderTone(slide);
      }, { once: true });
      return;
    }

    var key = slide.getAttribute('data-media-id');
    if (key && Object.prototype.hasOwnProperty.call(headerTone, key)) {
      setHeaderReversed(headerTone[key]);
      return;
    }
    measureSlide(slide, function (reversed) {
      if (key) headerTone[key] = reversed;
      // Only if this slide is still the one showing — a fast swipe can land on another
      // before the sample loads.
      if (slide.dataset.headerActive === 'true') setHeaderReversed(reversed);
    });
  }

  function initHeaderTone(root) {
    var list = root.querySelector('.js-v2-media-list');
    if (!list || !('IntersectionObserver' in window)) return;

    var mobile = window.matchMedia(MOBILE);

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          entry.target.dataset.headerActive = entry.isIntersecting ? 'true' : 'false';
          if (entry.isIntersecting && mobile.matches) applyHeaderTone(entry.target);
        });
      },
      // Past halfway in: the slide that owns the strip under the header, mid-swipe included.
      { root: list, threshold: 0.5 }
    );

    Array.prototype.forEach.call(list.children, function (slide) {
      observer.observe(slide);
    });

    var onChange = function () {
      if (!mobile.matches) {
        setHeaderReversed(false);
        return;
      }
      var active = list.querySelector('[data-header-active="true"]');
      if (active) applyHeaderTone(active);
    };

    if (typeof mobile.addEventListener === 'function') {
      mobile.addEventListener('change', onChange);
    } else if (typeof mobile.addListener === 'function') {
      mobile.addListener(onChange);
    }
  }

  /*
   * Mobile shows one slide at a time, so slide 2 starts off screen and stays lazy: fetching
   * it up front would compete with the lead image. Once the page has loaded, any slide within
   * one slide's width of the visible one loads ahead of the swipe, so the next image is
   * already there when the shopper gets to it. Desktop's peek already pulls slide 2 in, and
   * there the list is Splide's transformed track rather than a scroller, so every slide
   * would count as near — hence mobile only.
   */
  function initSlidePrefetch(root) {
    var list = root.querySelector('.js-v2-media-list');
    if (!list || !('IntersectionObserver' in window)) return;

    var start = function () {
      if (!window.matchMedia(MOBILE).matches) return;
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
              img.loading = 'eager';
            });
            observer.unobserve(entry.target);
          });
        },
        { root: list, rootMargin: '0px 100%' }
      );
      Array.prototype.forEach.call(list.children, function (slide) {
        observer.observe(slide);
      });
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }
  }

  /*
   * Desktop columns: whichever of the gallery and the buy box is shorter rides alongside the
   * other as the page scrolls, so neither column ends in a run of blank white. Both get the
   * same offset; the taller one is as tall as the row, so sticking does nothing to it.
   *
   * One offset covers both cases: top = min(header height, viewport - element height). An
   * element that fits pins under the (fixed) header; one that does not gets a negative top,
   * which pins it the moment its foot reaches the bottom of the viewport — the stylesheet's
   * `bottom: 0` behaviour for the panel, so every row stays reachable. Re-measured whenever
   * either resizes (an accordion opening, a variant swap, images loading) or the window
   * does. Without this file the stylesheet's own sticky offsets still apply.
   */
  function initStickyColumns(root) {
    var els = [root.querySelector('.product-v2__gallery'), root.querySelector('.product-v2__panel')].filter(Boolean);
    if (!els.length) return;
    var mq = window.matchMedia(MOBILE);

    function update() {
      var header = document.querySelector('.header');
      var headerHeight = header ? header.offsetHeight : 0;
      els.forEach(function (el) {
        if (mq.matches) {
          el.style.top = '';
          el.style.bottom = '';
          return;
        }
        el.style.top = Math.min(headerHeight, window.innerHeight - el.offsetHeight) + 'px';
        el.style.bottom = 'auto';
      });
    }

    update();
    window.addEventListener('resize', update);
    if (window.ResizeObserver) {
      var observer = new ResizeObserver(update);
      els.forEach(function (el) {
        observer.observe(el);
      });
    }
  }

  function init() {
    document.querySelectorAll('.product-v2').forEach(function (root) {
      var list = root.querySelector('.js-v2-media-list');
      // Recorded here as well as in initDots, which bails early on a single-medium product
      // or when the dot row is absent.
      if (list && !list.v2Order) list.v2Order = Array.prototype.slice.call(list.children);
      initDots(root);
      initHeaderTone(root);
      initSlidePrefetch(root);
      initStickyColumns(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
